import React, { useEffect, useState } from "react";
import { Box, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchWorkerDetail,
  resendOnboarding,
} from "store/slices/worker";
import AppDialog from "components/Dialog/AppDialog";
import ComponentLoading from "components/Common/ComponentLoading";
import StaffOnboarding from "./StaffOnboarding";
import PersonalTab from "./tabs/PersonalTab";
import WorkPayTab from "./tabs/WorkPayTab";
import ComplianceTab from "./tabs/ComplianceTab";
import DocumentsTab from "./tabs/DocumentsTab";
import AppraisalTab from "./tabs/AppraisalTab";
import AppColor from "utilities/AppColor";
import AdminFeedbackForm from "views/User/OnBoard/AdminFeedbackForm";
import { fetchWorkerFeedback } from "store/slices/worker";
import { verifyWorker } from "store/slices/worker";
import AppConfig from "config/AppConfig";
import { getAuthInfo } from "store/slices/auth";
import { SYSTEM_ROLE } from "config/AppConfig";
const { APPROVAL_STATUS } = AppConfig;

const useStyles = makeStyles(() => ({
  container: {
    padding: "1rem",
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: "#0D9488",
    marginBottom: 4,
  },
  breadcrumb: {
    fontSize: 12,
    color: "#6B7280",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  breadcrumbLink: {
    color: "#0D9488",
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    padding: 0,
    overflow: "hidden",
  },
  tabsContainer: {
    display: "flex",
    borderBottom: "1px solid #E5E7EB",
    flexWrap: "wrap",
  },
  tab: {
    padding: "9px 16px",
    fontSize: 12,
    color: "#6B7280",
    cursor: "pointer",
    borderBottom: "2.5px solid transparent",
    marginBottom: -1,
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#111827",
    },
  },
  tabActive: {
    color: "#0D9488",
    borderBottomColor: AppColor.mainColor,
    fontWeight: 600,
  },
  tabContent: {
    padding: 16,
  },
  footer: {
    padding: "12px 16px",
    borderTop: "1px solid #E5E7EB",
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
  },
  boxActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem 0.5rem",
  },
}));

const StaffDetail = ({ workerId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const params = useParams();
  const authInfo = useSelector(getAuthInfo) || {};
  const isAdmin = authInfo.role === SYSTEM_ROLE.COMPANY;

  const id = params.id || workerId;

  const [loading, setLoading] = useState(false);
  const [staffDetail, setStaffDetail] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [workerFeedback, setWorkerFeedback] = useState([]);
  const isMounted = React.useRef(true);

  const tabs = [
    { label: "Personal", component: <PersonalTab staffDetail={staffDetail} onChange={setStaffDetail} /> },
    { label: "Work & pay", component: <WorkPayTab staffDetail={staffDetail} onChange={setStaffDetail} /> },
    { label: "Documents", component: <DocumentsTab staffDetail={staffDetail} onChange={setStaffDetail} /> },
    { label: "Compliance", component: <ComplianceTab staffDetail={staffDetail} onChange={setStaffDetail} /> },
    { label: "Appraisal", component: <AppraisalTab staffDetail={staffDetail} onChange={setStaffDetail} /> },
  ];
  const isShowFeedback = staffDetail?.approvalStatus === APPROVAL_STATUS.PENDING || staffDetail?.approvalStatus === APPROVAL_STATUS.WAITING_FOR_APPROVAL || staffDetail?.approvalStatus === APPROVAL_STATUS.REJECT;

  useEffect(() => {
    isMounted.current = true;
    if (id) {
      loadStaffDetail();
      loadWorkerFeedback();
    }
    return () => {
      isMounted.current = false;
    };
  }, [id]);

  const loadStaffDetail = async () => {
    setLoading(true);
    try {
      const result = await dispatch(fetchWorkerDetail({ workerId: id }));
      if (!isMounted.current) return;
      if (result.payload) {
        setStaffDetail({ ...result.payload });
      } else {
        history.push("/admin/workers");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadWorkerFeedback = async () => {
    if (id) {
      setLoading(true);
      try {
        const result = await dispatch(fetchWorkerFeedback({ workerId: id }));
        if (!isMounted.current) return;
        if (result.payload) {
          setWorkerFeedback([...result.payload]);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRejectForm = async (form) => {
    const { feedbackId, ...rest } = form;
    const params = {
      ...rest,
      workerId: id,
      isRejected: feedbackId ? false : true
    };
    setLoading(true);
    try {
      const result = await dispatch(verifyWorker(params));
      if (result.payload) {
        await loadStaffDetail();
        await loadWorkerFeedback();
      }
    } finally {
      setLoading(false);
    }
  };

  // const handleCancel = () => {
  //   history.push("/admin/workers");
  // };

  // const handleSave = () => {
  //   dispatch(showToast({ type: "success", message: "Changes saved successfully!" }));
  //   history.push("/admin/workers");
  // };

  const handleResendOnboarding = async () => {
    if (staffDetail && staffDetail.id) {
      setLoading(true);
      try {
        await dispatch(resendOnboarding({
          domain: window.location.hostname,
          workerId: staffDetail.id
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.breadcrumb}>
        <span className={classes.breadcrumbLink} onClick={() => history.push("/admin/workers")}>
          {t("label.staff")}
        </span>
        <span>{'>'}</span>
        <span style={{ color: "#0D9488", fontWeight: 500 }}>
          {staffDetail?.firstName} {staffDetail?.lastName}
        </span>
      </Box>

      <Grid container justifyContent="center" spacing={2}>
        <Grid item xs={12} sm={4} md={3}>
          <StaffOnboarding
            staffDetail={staffDetail}
            onResendOnboarding={handleResendOnboarding}
            onChange={setStaffDetail}
          />
          {
            isShowFeedback && (workerFeedback?.[0] || isAdmin) && (
              <Box mt={2}>
                <Paper className={classes.card} style={{ padding: 16 }}>
                  <AdminFeedbackForm
                    onSubmit={isAdmin ? handleRejectForm : undefined}
                    isWorker={true}
                    display={isShowFeedback}
                    feedback={workerFeedback[0]}
                  />
                </Paper>
              </Box>
            )
          }
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Paper className={classes.card}>
            <Box className={classes.tabsContainer}>
              {tabs.map((tab, index) => (
                <Box
                  key={index}
                  className={`${classes.tab} ${activeTab === index ? classes.tabActive : ""}`}
                  onClick={() => setActiveTab(index)}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>
            <Box className={classes.tabContent}>
              {tabs[activeTab].component}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <AppDialog
        open={loading}
        onClose={() => setLoading(false)}
        noTitle
        noCloseButton
        disableEscapeKeyDown
      >
        <Box my={3}>
          <ComponentLoading />
        </Box>
      </AppDialog>
    </Box>
  );
};

export default StaffDetail;
